/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRentalItemById, updateRentalItem } from "../../../../services/rentalService";

const DeleteConfirmationModal = ({ isOpen, onCancel, onConfirm, title, message }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="p-6">
                    <div className="flex items-center space-x-3 text-red-600 mb-4">
                        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                            <i className="fi fi-rr-triangle-warning text-xl"></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-500 text-sm font-medium leading-relaxed">{message}</p>
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                    <button
                        onClick={onCancel}
                        className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-white transition-all active:scale-95"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-8 py-2.5 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 active:scale-95"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

const toList = (value) => {
    if (!value) return [];
    return String(value)
        .replace(/\r\n/g, "\n")
        .split("\n")
        .map((s) => s.replace(/^\s*-\s*/, "").trim())
        .filter(Boolean);
};

const toPayloadString = (items) => items.map((s) => `- ${String(s).trim()}`).join("\n");

const RentalInclusions = ({ rentalId, registerSave, onValidityChange }) => {
    const queryClient = useQueryClient();
    const [text, setText] = useState("");
    const [items, setItems] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, index: null });

    const { data: rentalData, isLoading } = useQuery({
        queryKey: ["rentalItem", rentalId],
        queryFn: () => getRentalItemById(rentalId),
        enabled: !!rentalId,
    });

    useEffect(() => {
        const d = rentalData?.data;
        if (!d) return;
        setItems(toList(d.inclusions));
    }, [rentalData]);

    const canSave = useMemo(() => true, []);
    useEffect(() => onValidityChange?.(canSave), [canSave]);

    const saveMutation = useMutation({
        mutationFn: (payload) => updateRentalItem(rentalId, payload),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["rentalItem", rentalId] });
        },
    });

    const handleAdd = () => {
        const t = String(text || "").trim();
        if (!t) return;
        setItems((prev) => [...prev, t]);
        setText("");
    };

    const confirmDelete = () => {
        if (deleteModal.index == null) return;
        setItems((prev) => prev.filter((_, i) => i !== deleteModal.index));
        setDeleteModal({ isOpen: false, index: null });
    };

    const handleSave = async () => {
        if (!rentalId) return;
        setIsSaving(true);
        try {
            await saveMutation.mutateAsync({
                inclusions: toPayloadString(items),
            });
        } catch (error) {
            throw error;
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        registerSave?.(() => handleSave());
    }, [registerSave, rentalId, items]);

    if (!rentalId) {
        return (
            <div className="bg-white border-2 border-dashed border-gray-200 p-12 rounded-3xl text-gray-500 flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4 text-gray-300">
                    <i className="fi fi-rr-lock text-2xl"></i>
                </div>
                <h4 className="font-bold text-gray-900 text-lg tracking-tight">Setup Required</h4>
                <p className="text-sm opacity-60 max-w-xs mt-2 font-medium">Please save General Info first.</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center p-20 text-gray-400">
                <i className="fi fi-rr-spinner animate-spin text-4xl mb-4"></i>
                <span className="font-bold uppercase tracking-widest text-[11px]">Loading...</span>
            </div>
        );
    }

    return (
        <div className="w-full space-y-4">
            <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3 flex gap-3">
                <i className="fi fi-rr-info text-amber-600 mt-0.5 shrink-0" aria-hidden />
                <p className="text-xs text-amber-900/80 leading-relaxed">
                    Optional step — list what is included with this rental. Use{" "}
                    <span className="font-semibold">Skip</span> at the bottom if you do not need inclusions.
                </p>
            </div>

            {isSaving ? (
                <div className="flex justify-end">
                    <span className="text-xs text-gray-400">Saving...</span>
                </div>
            ) : null}

            <div className="rounded-xl border border-gray-100 bg-slate-50/40 overflow-hidden">
                <div className="p-5 space-y-6">
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Add inclusion</label>
                        <div className="flex gap-3">
                            <input
                                type="text"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="Type inclusion..."
                                className="flex-1 h-8 text-sm px-3 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary transition-all font-medium"
                                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                            />
                            <button
                                onClick={handleAdd}
                                disabled={!String(text || "").trim()}
                                className="bg-primary text-white text-[10px] px-4 h-8 rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-all font-black uppercase tracking-widest shadow-lg shadow-primary/20 active:scale-95"
                            >
                                Add
                            </button>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Current inclusions</label>
                        <div className="space-y-2.5">
                            {items.length === 0 ? (
                                <div className="bg-gray-50/50 border border-dashed border-gray-200 rounded-2xl py-8 flex flex-col items-center justify-center text-gray-400">
                                    <i className="fi fi-rr-layers text-2xl mb-1 opacity-20"></i>
                                    <p className="text-xs italic">No inclusions added</p>
                                </div>
                            ) : (
                                items.map((item, index) => (
                                    <div
                                        key={`${item}-${index}`}
                                        className="w-full flex justify-between items-center bg-white hover:bg-gray-50 px-4 py-2 rounded-lg border border-gray-100 hover:border-gray-200 transition-all group"
                                    >
                                        <div className="flex items-center space-x-3">
                                            <div className="w-2 h-2 rounded-full bg-primary/40 group-hover:bg-primary transition-colors"></div>
                                            <span className="text-sm text-gray-700 font-semibold">{item}</span>
                                        </div>
                                        <button
                                            onClick={() => setDeleteModal({ isOpen: true, index })}
                                            className="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                        >
                                            <i className="fi fi-rr-trash text-sm"></i>
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <DeleteConfirmationModal
                isOpen={deleteModal.isOpen}
                onCancel={() => setDeleteModal({ isOpen: false, index: null })}
                onConfirm={confirmDelete}
                title="Delete Inclusion"
                message="Are you sure you want to delete this inclusion? This action cannot be undone."
            />
        </div>
    );
};

export default RentalInclusions;

